import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Search,
  Filter,
  Shield,
  ArrowRight,
  Check,
  X,
  Play,
  Eye,
} from 'lucide-react'
import Card from '@/components/Card'
import SectionHead from '@/components/SectionHead'
import HarvestFunnel from '@/components/HarvestFunnel'
import DateRangePicker from '@/components/DateRangePicker'
import { Badge } from '@/components/StatusBadge'
import type { BadgeTone } from '@/components/StatusBadge'
import {
  useHarvestFunnel,
  useHarvestSearchTerms,
  useHarvestPipeline,
  useHarvestNegatives,
  useApprovePipelineEntry,
  useRejectPipelineEntry,
  useApplyPipelineEntry,
  type HarvestRecommendation,
  type HarvestMatchType,
  type HarvestPipelineEntry,
  type HarvestSearchTerm,
  type HarvestNegative,
} from '@/api/harvest'
import { diffDays, isoDaysAgo, isoToday, useDateRange } from '@/lib/dateRange'
import { IS_DEMO_MODE } from '@/lib/demoMode'
import { cn } from '@/lib/utils'
import InboxHint from '@/components/InboxHint'

type TabId = 'terms' | 'pipeline' | 'negatives'

const VALID_TABS: ReadonlyArray<TabId> = ['terms', 'pipeline', 'negatives']

const RECOMMENDATION_LABELS: Record<HarvestRecommendation, { label: string; tone: BadgeTone }> = {
  promote: { label: 'Promote', tone: 'good' },
  negate: { label: 'Negate', tone: 'bad' },
  ignore: { label: 'Ignore', tone: 'neutral' },
}

const MATCH_TYPE_LABELS: Record<HarvestMatchType, { label: string; tone: BadgeTone }> = {
  exact: { label: 'Exact', tone: 'good' },
  phrase: { label: 'Phrase', tone: 'info' },
  broad: { label: 'Broad', tone: 'cool' },
}

const fmtEur = (n: number) =>
  n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'

function fmtAcos(n: number | null): string {
  if (n === null || n === 0) return '—'
  return `${(n * 100).toFixed(1)}%`
}

export default function HarvestWorkspace() {
  const [params, setParams] = useSearchParams()
  const rawTab = params.get('tab')
  const tab: TabId = (VALID_TABS as ReadonlyArray<string>).includes(rawTab ?? '')
    ? (rawTab as TabId)
    : 'terms'

  const { range, setRange } = useDateRange({ fallbackDays: 7 })

  const { data: funnelData } = useHarvestFunnel(range)
  const { data: termsData, isLoading: termsLoading } = useHarvestSearchTerms({ range })
  const { data: pipelineData, isLoading: pipelineLoading } = useHarvestPipeline(range)
  const { data: negativesData, isLoading: negativesLoading } = useHarvestNegatives({ range })

  const rangeDays = diffDays(range)
  const rangeLabel = `letzte ${rangeDays} Tage`
  const todayIso = isoToday()
  const isPresetWindow = range.to === todayIso && range.from === isoDaysAgo(rangeDays)

  const funnel = funnelData?.funnel
  const terms = useMemo(() => termsData?.searchTerms ?? [], [termsData])
  const pipeline = useMemo(() => pipelineData?.pipeline ?? [], [pipelineData])
  const negatives = useMemo(() => negativesData?.negatives ?? [], [negativesData])

  function setTab(next: TabId) {
    const newParams = new URLSearchParams(params)
    if (next === 'terms') newParams.delete('tab')
    else newParams.set('tab', next)
    setParams(newParams, { replace: true })
  }

  const tabCounts: Record<TabId, number> = {
    terms: terms.length,
    pipeline: pipeline.length,
    negatives: negatives.length,
  }

  const tabMeta: Array<{ id: TabId; label: string; icon: typeof Search }> = [
    { id: 'terms', label: 'Suchbegriffe', icon: Search },
    { id: 'pipeline', label: 'Keyword-Pipeline', icon: Filter },
    { id: 'negatives', label: 'Negatives-Workspace', icon: Shield },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-[var(--foreground)]">
            Auto-Harvest
          </h1>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            Auto-Kampagnen ausschlachten · Suchbegriffe klassifizieren · in passende Match-Types
            überführen oder als Negative blockieren.
          </p>
        </div>
        <DateRangePicker value={range} onChange={setRange} />
      </div>

      {IS_DEMO_MODE && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          <strong className="font-bold">Demo-Modus:</strong> Approve/Apply/Negate sind
          UI-Aktionen — kein Schreibzugriff zur Amazon API.
        </div>
      )}

      <Card className="p-4">
        <SectionHead
          title="Pipeline-Trichter"
          subtitle={`vom Auto-Sync zur Aktion · ${isPresetWindow ? rangeLabel : `${range.from} → ${range.to}`}`}
          className="mb-3"
        />
        <HarvestFunnel
          steps={[
            { key: 'Discovery', value: funnel?.discovery ?? '–', description: `neue Search-Terms ${rangeDays}T`, tone: 'neutral' },
            { key: 'Candidates', value: funnel?.candidates ?? '–', description: 'noch nicht entschieden', tone: 'info' },
            { key: 'Promoted', value: funnel?.promoted ?? '–', description: `${rangeDays} T`, tone: 'good' },
            { key: 'Negated', value: funnel?.negated ?? '–', description: `${rangeDays} T`, tone: 'bad' },
          ]}
        />
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--border)]">
        {tabMeta.map((t) => {
          const active = tab === t.id
          const Icon = t.icon
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors',
                active
                  ? 'border-orange-500 text-[var(--foreground)]'
                  : 'border-transparent text-[var(--muted-foreground)] hover:text-[var(--foreground)]',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
              <span
                className={cn(
                  'ml-1 inline-flex items-center justify-center rounded-full px-1.5 text-[10.5px] font-bold',
                  active
                    ? 'bg-orange-500 text-white'
                    : 'bg-[var(--border)] text-[var(--muted-foreground)]',
                )}
              >
                {tabCounts[t.id]}
              </span>
            </button>
          )
        })}
      </div>

      {tab === 'terms' && <SearchTermsTab terms={terms} loading={termsLoading} />}
      {tab === 'pipeline' && <PipelineTab pipeline={pipeline} loading={pipelineLoading} />}
      {tab === 'negatives' && <NegativesTab negatives={negatives} loading={negativesLoading} />}
    </div>
  )
}

// --- Tab: Suchbegriffe ---

function SearchTermsTab({ terms, loading }: { terms: HarvestSearchTerm[]; loading: boolean }) {
  return (
    <Card className="overflow-hidden p-0">
      {loading ? (
        <div className="px-5 py-10 text-center text-sm text-[var(--muted-foreground)]">
          Lade Suchbegriffe…
        </div>
      ) : terms.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-[var(--muted-foreground)]">
          Keine Suchbegriffe gefunden.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--muted)]/40 text-left">
                {['Search Term', 'Empfehlung', 'Vorschlag', 'Clicks', 'Orders', 'CVR', 'Spend', 'Sales', 'ACoS', 'Quelle'].map((h, i) => (
                  <th
                    key={h}
                    className={cn(
                      'px-3 py-2 text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]',
                      i >= 3 && i <= 8 && 'text-right',
                    )}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {terms.map((t) => {
                const rec = RECOMMENDATION_LABELS[t.recommendation]
                const suggested = MATCH_TYPE_LABELS[t.suggestedMatchType]
                const cvr = t.clicks > 0 ? (t.orders / t.clicks) * 100 : 0
                const acosTone =
                  t.acos === null || t.acos === 0
                    ? 'text-[var(--muted-foreground)]'
                    : t.acos > 0.5
                      ? 'text-red-600'
                      : t.acos > 0.3
                        ? 'text-amber-600'
                        : 'text-emerald-600'
                return (
                  <tr key={t.id} className="border-b border-[var(--border)] last:border-b-0">
                    <td className="max-w-[280px] truncate px-3 py-2 font-mono text-xs text-[var(--foreground)]">
                      {t.term}
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={rec.tone} size="sm">
                        {rec.label}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      {t.recommendation === 'promote' ? (
                        <div className="flex flex-col gap-0.5 text-[11.5px] font-semibold">
                          <span className="inline-flex items-center gap-1">
                            <ArrowRight className="h-3 w-3 text-[var(--muted-foreground)]" />
                            <Badge tone={suggested.tone} size="sm">
                              {suggested.label}
                            </Badge>
                            {t.suggestedBid !== null && (
                              <span className="ml-1 text-[10.5px] text-[var(--muted-foreground)]">
                                @ {t.suggestedBid.toFixed(2)} €
                              </span>
                            )}
                          </span>
                          {t.suggestedTargetCampaignName !== null ? (
                            <span
                              className="max-w-[220px] truncate font-mono text-[10.5px] font-normal text-[var(--muted-foreground)]"
                              title={t.suggestedTargetCampaignName}
                            >
                              → {t.suggestedTargetCampaignName}
                            </span>
                          ) : (
                            <span className="text-[10.5px] font-normal italic text-[var(--muted-foreground)]">
                              kein eindeutiges Ziel
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[var(--muted-foreground)]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{t.clicks}</td>
                    <td
                      className={cn(
                        'px-3 py-2 text-right tabular-nums',
                        t.orders > 0 && 'font-bold',
                      )}
                    >
                      {t.orders}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-[var(--muted-foreground)]">
                      {cvr.toFixed(1)}%
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtEur(t.spend)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtEur(t.sales)}</td>
                    <td className={cn('px-3 py-2 text-right tabular-nums font-semibold', acosTone)}>
                      {fmtAcos(t.acos)}
                    </td>
                    <td className="max-w-[180px] truncate px-3 py-2 font-mono text-[11px] text-[var(--muted-foreground)]">
                      {t.sourceCampaignName}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

// --- Tab: Pipeline ---

function PipelineTab({ pipeline, loading }: { pipeline: HarvestPipelineEntry[]; loading: boolean }) {
  const approveMutation = useApprovePipelineEntry()
  const rejectMutation = useRejectPipelineEntry()
  const applyMutation = useApplyPipelineEntry()
  const isMutating = approveMutation.isPending || rejectMutation.isPending || applyMutation.isPending
  return (
    <>
      <InboxHint className="mb-3" />
      <Card className="overflow-hidden p-0">
      {loading ? (
        <div className="px-5 py-10 text-center text-sm text-[var(--muted-foreground)]">
          Lade Pipeline…
        </div>
      ) : pipeline.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-[var(--muted-foreground)]">
          Keine Pipeline-Einträge.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="border-b border-[var(--border)] bg-[var(--muted)]/40 text-left">
                {['Search Term', 'Match Type', 'Ziel-Kampagne', 'Bid', 'Promoted', 'Status', 'Aktionen'].map((h, i) => (
                  <th
                    key={h}
                    className={cn(
                      'px-3 py-2 text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]',
                      i === 3 ? 'text-right' : i >= 5 ? 'text-center' : '',
                    )}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pipeline.map((p) => {
                const matchLabel = MATCH_TYPE_LABELS[p.matchType]
                return (
                  <tr key={p.id} className="border-b border-[var(--border)] last:border-b-0">
                    <td className="max-w-[240px] truncate px-3 py-2 font-mono text-xs text-[var(--foreground)]">
                      {p.term}
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={matchLabel.tone} size="sm">
                        {matchLabel.label}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 font-mono text-[11.5px] text-[var(--foreground)]">
                      {p.targetCampaign.name}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {p.bid !== null ? `${p.bid.toFixed(2)} €` : '—'}
                    </td>
                    <td className="px-3 py-2 text-[11.5px] text-[var(--muted-foreground)]">
                      {new Date(p.promotedAt).toLocaleString('de-DE', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {/* Spine-3-Integration: reconcile_required = pruefbeduerftig (rot),
                          applying = laufender Apply (neutral, transient). */}
                      <Badge
                        tone={p.status === 'reconcile_required' ? 'bad' : p.status === 'applying' ? 'neutral' : 'warn'}
                        size="sm"
                      >
                        {p.status === 'reconcile_required' ? 'prüfbedürftig' : p.status === 'applying' ? 'wird angewendet' : p.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="inline-flex gap-1">
                        {p.status === 'pending' && (
                          <>
                            <IconBtn
                              title="Genehmigen"
                              tone="emerald"
                              disabled={isMutating}
                              onClick={() => approveMutation.mutate(p.id)}
                            >
                              <Check className="h-3.5 w-3.5" />
                            </IconBtn>
                            <IconBtn
                              title="Ablehnen"
                              tone="red"
                              disabled={isMutating}
                              onClick={() => rejectMutation.mutate(p.id)}
                            >
                              <X className="h-3.5 w-3.5" />
                            </IconBtn>
                          </>
                        )}
                        {/* Spine-3-Integration (Fenster I / GLB-1): reconcile_required ist
                            über approve ("Amazon geprüft, kein Write → erneut freigeben")
                            oder reject auflösbar; das Backend akzeptiert beide (WHERE
                            harvest_status != 'applying' + In-Flight-Guard). Sonst bliebe ein
                            Harvest-Apply mit unklarem Amazon-Ausgang gestrandet. */}
                        {p.status === 'reconcile_required' && (
                          <>
                            <IconBtn
                              title="Amazon geprüft — erneut freigeben"
                              tone="emerald"
                              disabled={isMutating}
                              onClick={() => approveMutation.mutate(p.id)}
                            >
                              <Check className="h-3.5 w-3.5" />
                            </IconBtn>
                            <IconBtn
                              title="Geprüft — ablehnen/verwerfen"
                              tone="red"
                              disabled={isMutating}
                              onClick={() => rejectMutation.mutate(p.id)}
                            >
                              <X className="h-3.5 w-3.5" />
                            </IconBtn>
                          </>
                        )}
                        {/* applying: laufender Apply-Claim — keine Entscheidung (Backend
                            lehnt sie mit 409 ab, bis der Apply-Ausgang feststeht). */}
                        {p.status === 'applying' && (
                          <IconBtn title="Apply läuft — Ausgang abwarten" tone="muted" disabled>
                            <Eye className="h-3.5 w-3.5" />
                          </IconBtn>
                        )}
                        {p.status === 'approved' && (
                          <IconBtn
                            title={IS_DEMO_MODE ? 'Im Demo-Modus deaktiviert (Amazon-API-Push)' : 'Ausführen'}
                            tone="blue"
                            disabled={IS_DEMO_MODE || isMutating}
                            onClick={() => applyMutation.mutate(p.id)}
                          >
                            <Play className="h-3.5 w-3.5" />
                          </IconBtn>
                        )}
                        {p.status === 'applied' && (
                          <IconBtn title="Detail" tone="muted" disabled>
                            <Eye className="h-3.5 w-3.5" />
                          </IconBtn>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      </Card>
    </>
  )
}

// --- Tab: Negatives ---

function NegativesTab({
  negatives,
  loading,
}: {
  negatives: HarvestNegative[]
  loading: boolean
}) {
  return (
    <div className="space-y-3">
      <Card className="overflow-hidden p-0">
        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-[var(--muted-foreground)]">
            Lade Negatives…
          </div>
        ) : negatives.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-[var(--muted-foreground)]">
            Keine Negatives gefunden.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--muted)]/40 text-left">
                  {['Keyword', 'Negative-Typ', 'Scope', 'Kampagne', 'Status', 'Erstellt'].map(
                    (h, i) => (
                      <th
                        key={h}
                        className={cn(
                          'px-3 py-2 text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]',
                          i === 4 ? 'text-center' : '',
                        )}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {negatives.map((n) => (
                  <tr key={n.id} className="border-b border-[var(--border)] last:border-b-0">
                    <td className="px-3 py-2 font-mono text-xs text-[var(--foreground)]">
                      {n.keywordText}
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone="bad" size="sm">
                        {n.matchType === 'negative_exact' ? 'Negative Exact' : 'Negative Phrase'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {n.scope === 'ad-group' ? 'Ad Group' : 'Campaign'}
                    </td>
                    <td className="max-w-[200px] truncate px-3 py-2 font-mono text-[11px] text-[var(--muted-foreground)]">
                      {n.campaignName ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Badge tone={n.state === 'enabled' ? 'good' : 'neutral'} size="sm">
                        {n.state}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-[11.5px] text-[var(--muted-foreground)]">
                      {new Date(n.createdAt).toLocaleString('de-DE', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

function IconBtn({
  title,
  tone,
  children,
  onClick,
  disabled,
}: {
  title: string
  tone: 'emerald' | 'red' | 'blue' | 'muted'
  children: React.ReactNode
  onClick?: () => void
  disabled?: boolean
}) {
  const colors: Record<typeof tone, string> = {
    emerald: 'text-emerald-600 hover:bg-emerald-50',
    red: 'text-red-600 hover:bg-red-50',
    blue: 'text-blue-600 hover:bg-blue-50',
    muted: 'text-[var(--muted-foreground)] hover:bg-[var(--muted)]',
  }
  // v29 Phase A: disabled wird explizit übergeben — Approve/Reject sind nicht mehr
  // pauschal an IS_DEMO_MODE gekoppelt (DB-only Mutation, keine Amazon-API).
  // Default-Verhalten für unmigierte Aufrufer: weiterhin IS_DEMO_MODE-gebunden.
  const isDisabled = disabled === undefined ? IS_DEMO_MODE : disabled
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={isDisabled}
      data-testid={`pipeline-icon-btn-${tone}`}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-md border border-[var(--border)] transition-colors',
        isDisabled ? 'cursor-not-allowed opacity-50' : colors[tone],
      )}
    >
      {children}
    </button>
  )
}
